import { ChevronDown, ChevronUp } from 'lucide-react'

import TooltipWrapper from 'components/reusable/TooltipWrapper'
import { DropdownMenu } from 'slab'

export type SplitFilterButtonOption = {
  label: string
  handleSelect: () => void
}

export type SplitFilterButtonProps = {
  primaryOption: SplitFilterButtonOption
  additionalOptions: SplitFilterButtonOption[]
  variant?: 'primary' | 'tertiary'
  tooltip?: string
}

/**
 * A split button component that combines a primary action button with a dropdown menu
 * for related filter options.
 *
 * @example
 * ```tsx
 * <SplitFilterButton
 *   variant='primary'
 *   tooltip='Filter workflows by outstanding tasks'
 *   primaryOption={{
 *     label: 'Outstanding Submission Tasks',
 *     handleSelect: () => applyFilter('submission'),
 *   }}
 *   additionalOptions={[
 *     { label: 'Outstanding Payment Tasks', handleSelect: () => applyFilter('payment') },
 *     { label: 'Outstanding Autopay Tasks', handleSelect: () => applyFilter('autopay') },
 *   ]}
 * />
 * ```
 */
export const SplitFilterButton = ({
  primaryOption,
  additionalOptions,
  variant = 'tertiary',
  tooltip,
}: SplitFilterButtonProps) => {
  // Color classes based on variant
  const colors = variant === 'primary'
    ? {
        text: 'text-thatch-500 hover:text-thatch-600',
        bg: 'hover:bg-thatch-100 active:bg-thatch-50',
        icon: 'text-thatch-400',
      }
    : {
        text: 'text-gray-500 hover:text-gray-600',
        bg: 'hover:bg-gray-100 active:bg-gray-50',
        icon: 'text-gray-400',
      }

  const handlePrimaryClick = () => {
    primaryOption.handleSelect()
  }

  const buttonContent = (
    <div className='inline-flex items-stretch rounded-lg overflow-hidden gap-0'>
      <button
        onClick={handlePrimaryClick}
        className={`h-8 py-1 pl-4 pr-2 inline-flex justify-center items-center rounded-l-lg text-sm font-semibold ${colors.text} ${colors.bg} transition outline-none`}
        type='button'
      >
        {primaryOption.label}
      </button>
      <DropdownMenu
        trigger={({ open }) => (
          <button
            className={`h-8 w-6 inline-flex items-center justify-center rounded-r-lg ${colors.icon} hover:text-gray-600 ${colors.bg} transition outline-none`}
            type='button'
            aria-label='More filter options'
          >
            {open
              ? <ChevronUp className='h-4 w-4' />
              : <ChevronDown className='h-4 w-4' />}
          </button>
        )}
        align='end'
      >
        {additionalOptions.map((option, index) => (
          <DropdownMenu.Item
            key={index}
            onSelect={option.handleSelect}
          >
            {option.label}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu>
    </div>
  )

  // Wrap in tooltip if provided
  if (tooltip) {
    return <TooltipWrapper tooltipText={tooltip}>{buttonContent}</TooltipWrapper>
  }

  return buttonContent
}
